import { useState } from "react";
import Modal from "@/components/base/Modal";
import { useToast } from "@/components/feature/ToastProvider";
import type { TugJudge, TugTeam } from "@/lib/types";
import { writeTug } from "../api";

interface Props {
  open: boolean;
  onClose: () => void;
  /** 성공 시 생성된 경기 번호와 «바로 참가할 팀» 전달 */
  onCreated: (no?: number, team?: TugTeam) => void;
}

const TAP_PRESETS = [
  { label: "10초", value: 10 },
  { label: "20초", value: 20 },
  { label: "30초", value: 30 },
];

const MIN_PRESETS = [
  { label: "30분", value: 30 },
  { label: "1시간", value: 60 },
  { label: "2시간", value: 120 },
];

// 승부 기준 칩 (기본은 «동수 상위합계»)
const JUDGE_MODES: { key: TugJudge; label: string; desc: string; badge?: string }[] = [
  {
    key: "top",
    label: "동수 상위합계",
    desc: "인원이 3:2 면 양 팀 상위 2명씩만 합산합니다. 가장 공정합니다",
    badge: "권장",
  },
  {
    key: "avg",
    label: "1인 평균",
    desc: "총합 ÷ 인원. 혼자 잘 두드린 한 명이 팀을 이기게 할 수 있습니다",
  },
  {
    key: "sum",
    label: "전원 합계",
    desc: "인원이 많은 팀이 거의 무조건 이깁니다. 인원을 맞출 수 있을 때만",
  },
];

// 인원차 허용 칩 (balance)
const BALANCE_PRESETS = [
  { label: "1명", value: 1 },
  { label: "2명", value: 2 },
  { label: "제한없음", value: 0 },
];

const inputClass =
  "w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

// «경기 열기» 등록 모달.
// 등록자는 자기 팀(A/B)을 먼저 정하고, 등록 직후 바로 두드리기 화면으로 넘어간다.
export default function TugWriteModal({ open, onClose, onCreated }: Props) {
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [teamA, setTeamA] = useState("A팀");
  const [teamB, setTeamB] = useState("B팀");
  const [hostTeam, setHostTeam] = useState<TugTeam | "">("");
  const [limitCnt, setLimitCnt] = useState(5);
  const [tapSec, setTapSec] = useState(20);
  const [limitMin, setLimitMin] = useState(30);
  const [judgeMode, setJudgeMode] = useState<TugJudge>("top");
  const [balance, setBalance] = useState(1);
  const [reward, setReward] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setSubject("");
    setTeamA("A팀");
    setTeamB("B팀");
    setHostTeam("");
    setLimitCnt(5);
    setTapSec(20);
    setLimitMin(30);
    setJudgeMode("top");
    setBalance(1);
    setReward("");
  };

  const close = () => {
    if (submitting) return;
    onClose();
  };

  const submit = async () => {
    const s = subject.trim();
    if (!s) {
      toast("경기 제목을 입력해 주세요.", "info");
      return;
    }
    if (!hostTeam) {
      toast("내 팀을 골라 주세요.", "info");
      return;
    }
    setSubmitting(true);
    try {
      const res = await writeTug({
        subject: s,
        team_a: teamA.trim() || "A팀",
        team_b: teamB.trim() || "B팀",
        host_team: hostTeam,
        limit_cnt: Math.max(0, Number(limitCnt) || 0),
        limit_min: Math.max(5, Number(limitMin) || 30),
        tap_sec: Math.max(5, Number(tapSec) || 20),
        judge_mode: judgeMode,
        balance,
        reward: reward.trim(),
      });
      toast(res.msg || "경기를 열었습니다!", "success");
      reset();
      onCreated(res.data?.no, hostTeam);
    } catch (e) {
      toast(e instanceof Error ? e.message : "등록에 실패했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={close} maxWidth="max-w-md" labelledBy="tug-write-title">
      <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
        <h2
          id="tug-write-title"
          className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950"
        >
          <span aria-hidden="true">🪢</span>
          경기 열기
        </h2>
        <button
          type="button"
          onClick={close}
          aria-label="닫기"
          className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
        >
          <i className="ri-close-line text-lg" aria-hidden="true"></i>
        </button>
      </div>

      <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-5">
        <div>
          <label
            htmlFor="tug-subject"
            className="mb-1.5 block text-sm font-semibold text-foreground-700"
          >
            경기 제목 <span className="text-accent-600">*</span>
          </label>
          <input
            id="tug-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={80}
            placeholder="예) 정기모임 줄다리기"
            className={inputClass}
          />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex-1">
            <label
              htmlFor="tug-team-a"
              className="mb-1.5 block text-sm font-semibold text-foreground-700"
            >
              A팀 이름
            </label>
            <input
              id="tug-team-a"
              type="text"
              value={teamA}
              onChange={(e) => setTeamA(e.target.value)}
              maxLength={20}
              placeholder="A팀"
              className={inputClass}
            />
          </div>
          <div className="flex-1">
            <label
              htmlFor="tug-team-b"
              className="mb-1.5 block text-sm font-semibold text-foreground-700"
            >
              B팀 이름
            </label>
            <input
              id="tug-team-b"
              type="text"
              value={teamB}
              onChange={(e) => setTeamB(e.target.value)}
              maxLength={20}
              placeholder="B팀"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-semibold text-foreground-700">
            내 팀 <span className="text-accent-600">*</span>
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(["A", "B"] as TugTeam[]).map((t) => {
              const selected = hostTeam === t;
              const label = (t === "A" ? teamA : teamB).trim() || `${t}팀`;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setHostTeam(t)}
                  aria-pressed={selected}
                  className={`min-h-[52px] rounded-lg border px-3 text-sm font-bold transition-colors ${
                    selected
                      ? t === "A"
                        ? "border-primary-400 bg-primary-50 text-primary-800"
                        : "border-accent-400 bg-accent-50 text-accent-900"
                      : "border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-foreground-400">
            등록자도 이 팀으로 참가합니다. 경기를 연 뒤 바로 두드리기 화면으로 넘어갑니다.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex-1">
            <label
              htmlFor="tug-count"
              className="mb-1.5 block text-sm font-semibold text-foreground-700"
            >
              팀당 인원
            </label>
            <input
              id="tug-count"
              type="number"
              min={0}
              max={99}
              value={limitCnt}
              onChange={(e) => setLimitCnt(Number(e.target.value))}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-foreground-400">0 이면 인원 제한 없음</p>
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-semibold text-foreground-700">두드리는 시간</p>
          <div className="flex flex-wrap gap-2">
            {TAP_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setTapSec(p.value)}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                  tapSec === p.value
                    ? "border-primary-400 bg-primary-50 text-primary-700"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-semibold text-foreground-700">리미트 시간</p>
          <div className="flex flex-wrap gap-2">
            {MIN_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setLimitMin(p.value)}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                  limitMin === p.value
                    ? "border-primary-400 bg-primary-50 text-primary-700"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-semibold text-foreground-700">승부 기준</p>
          <div className="grid grid-cols-3 gap-2">
            {JUDGE_MODES.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setJudgeMode(m.key)}
                aria-pressed={judgeMode === m.key}
                className={`relative min-h-[52px] rounded-lg border px-2 text-xs font-bold transition-colors ${
                  judgeMode === m.key
                    ? "border-primary-400 bg-primary-50 text-primary-800"
                    : "border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100"
                }`}
              >
                {m.label}
                {m.badge && (
                  <span className="ml-1 inline-block rounded-full bg-accent-100 px-1.5 py-0.5 text-[10px] font-bold text-accent-900">
                    {m.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-foreground-400">
            {JUDGE_MODES.find((m) => m.key === judgeMode)?.desc}
          </p>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-semibold text-foreground-700">인원차 허용</p>
          <div className="flex flex-wrap gap-2">
            {BALANCE_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setBalance(p.value)}
                aria-pressed={balance === p.value}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                  balance === p.value
                    ? "border-primary-400 bg-primary-50 text-primary-700"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-foreground-400">
            한 팀에 사람이 몰리는 걸 막습니다. 1명이면 인원차가 최대 1명까지만 납니다.
          </p>
        </div>

        <div>
          <label
            htmlFor="tug-reward"
            className="mb-1.5 block text-sm font-semibold text-foreground-700"
          >
            이긴 팀 보상 (선택)
          </label>
          <input
            id="tug-reward"
            type="text"
            value={reward}
            onChange={(e) => setReward(e.target.value)}
            maxLength={60}
            placeholder="비워두면 그냥 재미로 하는 게임입니다"
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex gap-2 border-t border-background-200 px-5 py-4">
        <button
          type="button"
          onClick={close}
          disabled={submitting}
          className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
        >
          취소
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600 disabled:opacity-60"
        >
          {submitting ? "여는 중…" : "경기 열기"}
        </button>
      </div>
    </Modal>
  );
}